import {
  CheckNetworkTCPConnectivityFeedback,
  CheckNetworkUDPConnectivityFeedback,
  ConsoleLogger,
  DefaultDeviceController,
  DefaultMeetingReadinessChecker,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionConfiguration,
} from 'amazon-chime-sdk-js';
import { CHECK_STATUS } from '@/features/diagnostics/types';

const bounded = async (operation, timeoutMs) => {
  let timer;
  try {
    return await Promise.race([
      operation,
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('TimedOut')), timeoutMs);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
};

export const checkChimeTransport = async ({
  chime, transport, timeoutMs, signal, cleanupErrors, onTeardownStarted,
}) => {
  let session;
  let deviceController;
  let originalRewriter;
  let pendingCheck;
  let stopped = true;
  let resolveStopped;
  const observer = {
    audioVideoDidStartConnecting: () => { stopped = false; },
    audioVideoDidStart: () => { stopped = false; },
    audioVideoDidStop: () => {
      stopped = true;
      resolveStopped?.();
    },
  };
  const stop = () => {
    try {
      session?.audioVideo.stop();
    } catch {
      cleanupErrors.push(`${transport}:StopFailed`);
      console.error('Chime diagnostic stop failed', transport);
    }
  };

  try {
    if (signal?.aborted) throw new Error('Cancelled');
    // SDK debug logs can contain meeting credentials. Report only named readiness feedback.
    const logger = new ConsoleLogger('NetworkReadiness', LogLevel.OFF);
    deviceController = new DefaultDeviceController(logger);
    const configuration = new MeetingSessionConfiguration(chime.Meeting, chime.Attendee);
    configuration.connectionTimeoutMs = timeoutMs;
    session = new DefaultMeetingSession(configuration, logger, deviceController);
    originalRewriter = configuration.urls.urlRewriter;
    session.audioVideo.addObserver(observer);
    signal?.addEventListener('abort', stop, { once: true });

    const checker = new DefaultMeetingReadinessChecker(logger, session, {
      timeoutMs,
      waitDurationMs: 100,
    });
    const feedbackEnum =
      transport === 'udp'
        ? CheckNetworkUDPConnectivityFeedback
        : CheckNetworkTCPConnectivityFeedback;
    stopped = false;
    pendingCheck =
      transport === 'udp'
        ? checker.checkNetworkUDPConnectivity()
        : checker.checkNetworkTCPConnectivity();
    // Each SDK check can wait for start, ICE metrics, and stop separately.
    const feedback = await bounded(pendingCheck, timeoutMs * 3 + 1000);
    if (signal?.aborted) throw new Error('Cancelled');
    return {
      status: feedback === feedbackEnum.Succeeded ? CHECK_STATUS.PASS : CHECK_STATUS.FAIL,
      feedback: feedbackEnum[feedback] || 'UnknownFeedback',
    };
  } catch {
    console.warn('Chime network diagnostic failed', transport);
    return {
      status: CHECK_STATUS.FAIL,
      feedback: signal?.aborted ? 'Cancelled' : 'ReadinessFailed',
    };
  } finally {
    signal?.removeEventListener('abort', stop);
    const cleanup = async () => {
      if (session) stop();
      // Timing out a promise does not cancel its SDK work. Keep shared event state
      // alive until the checker and device cleanup have actually finished.
      if (pendingCheck) {
        try {
          await pendingCheck;
        } catch {
          console.warn('Chime diagnostic check did not finish normally', transport);
        }
      }
      if (session) {
        session.configuration.urls.urlRewriter = originalRewriter;
        if (!stopped) {
          await new Promise((resolve) => {
            resolveStopped = resolve;
            stop();
          });
        }
        session.audioVideo.removeObserver(observer);
      }
      if (deviceController) {
        try {
          // Device destruction publishes input-unselected events through the
          // session's event controller, so the session must be destroyed last.
          await deviceController.destroy();
        } catch {
          cleanupErrors.push(`${transport}:DeviceCleanupFailed`);
          console.error('Chime diagnostic device cleanup failed', transport);
        }
      }
      if (session) {
        try {
          await session.destroy();
        } catch {
          cleanupErrors.push(`${transport}:SessionCleanupFailed`);
          console.error('Chime diagnostic session cleanup failed', transport);
        }
      }
    };
    const teardown = cleanup();
    onTeardownStarted?.(teardown);
    try {
      // Let event cancellation proceed on deadline, but never run dependent
      // destruction out of order. Deferred cleanup remains handled by this chain.
      await bounded(teardown, timeoutMs);
    } catch {
      cleanupErrors.push(`${transport}:CleanupUnconfirmed`);
      console.error('Chime diagnostic cleanup incomplete or unconfirmed', transport);
    }
  }
};
